import { useState, useCallback, useEffect } from 'react';
import { collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, where, Timestamp, runTransaction, serverTimestamp, type Transaction, writeBatch } from 'firebase/firestore';
import { httpsCallableFromURL } from 'firebase/functions';
import { db, functions, callableUrl } from '@/lib/firebase';
import { useBranchContext } from '@/contexts/BranchContext';
import { useAuditLog } from '@/hooks/useAuditLog';

// ... (Types remain same) ...


// Types
export type SubscriptionPlanType = 'punch_card' | 'recurring' | 'unlimited';

export interface SubscriptionPlan {
    id: string;
    name: string;
    /** The name Arabic-speaking members see (signup, member screens). Falls back to `name`. */
    nameAr?: string;
    description?: string;
    planType: SubscriptionPlanType;
    durationDays: number;
    classLimit: number | null; // null = unlimited
    price: number;
    isActive: boolean;
    isPublic?: boolean;
    isFeatured?: boolean; // New: Featured plan ribbon
    order?: number; // New: Display order
    createdAt: Date;
    updatedAt: Date;
}

export interface PromoCode {
    id: string;
    name: string; // Discount name
    description?: string; // Discount description
    code: string; // Keep for backward compatibility
    discountType: 'percentage' | 'fixed';
    discountValue: number;
    validFrom: Date;
    validUntil: Date;
    maxUses?: number;
    usedCount: number;
    applicablePlanIds?: string[];
    isActive: boolean;
    createdAt: Date;
}

export interface UserSubscription {
    id: string;
    userId: string;
    userName: string;
    planId: string;
    planName: string;
    planType: SubscriptionPlanType;
    startDate: Date;
    endDate: Date;
    classLimit: number | null;
    classesUsed: number;
    status: 'active' | 'expired' | 'cancelled';
    promoCodeUsed?: string;
    amountPaid: number;
    renewalNotified: boolean;
    branchId?: string;
    createdAt: Date;
}

/**
 * Hook for managing subscriptions, plans, and promo codes.
 * Handles creating plans, assigning subscriptions (with transactions), and tracking usage.
 */
export const useSubscriptions = () => {
    const { selectedBranch } = useBranchContext();
    const { logAction } = useAuditLog();
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
    const [userSubscriptions, setUserSubscriptions] = useState<UserSubscription[]>([]);
    const [loading, setLoading] = useState(true);

    // Helper to convert Firestore timestamps
    const toDate = (val: unknown): Date => {
        if (!val) return new Date();
        if (typeof val === 'object' && val !== null && 'toDate' in val && typeof (val as { toDate: () => Date }).toDate === 'function') {
            return (val as { toDate: () => Date }).toDate();
        }
        if (val instanceof Date) return val;
        return new Date(val as string | number);
    };

    // Fetch all plans
    const fetchPlans = useCallback(async () => {
        try {
            // NOTE: Removed orderBy('order') because it filters out documents missing the 'order' field (legacy plans).
            // We fetch all and sort in memory.
            const q = query(collection(db, 'subscriptionPlans'));
            const snapshot = await getDocs(q);
            const fetchedPlans: SubscriptionPlan[] = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                fetchedPlans.push({
                    id: doc.id,
                    name: data.name,
                    description: data.description,
                    planType: data.planType || 'recurring',
                    durationDays: data.durationDays,
                    classLimit: data.classLimit,
                    price: data.price,
                    isActive: data.isActive ?? true,
                    isPublic: data.isPublic,
                    isFeatured: data.isFeatured,
                    order: data.order ?? 0,
                    createdAt: toDate(data.createdAt),
                    updatedAt: toDate(data.updatedAt),
                });
            });

            // Sort by order (asc), then price (asc)
            fetchedPlans.sort((a, b) => {
                const orderA = a.order ?? 0;
                const orderB = b.order ?? 0;
                if (orderA !== orderB) return orderA - orderB;
                return a.price - b.price;
            });

            setPlans(fetchedPlans);
        } catch (err) {
            console.error('Error fetching plans:', err);
        }
    }, []);

    // Fetch promo codes
    const fetchPromoCodes = useCallback(async () => {
        try {
            const q = query(collection(db, 'promoCodes'), orderBy('code'));
            const snapshot = await getDocs(q);
            const fetched: PromoCode[] = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                fetched.push({
                    id: doc.id,
                    name: data.name || data.code, // Fallback to code for legacy data
                    description: data.description || '',
                    code: data.code,
                    discountType: data.discountType,
                    discountValue: data.discountValue,
                    validFrom: toDate(data.validFrom),
                    validUntil: toDate(data.validUntil),
                    maxUses: data.maxUses,
                    usedCount: data.usedCount || 0,
                    applicablePlanIds: data.applicablePlanIds,
                    isActive: data.isActive ?? true,
                    createdAt: toDate(data.createdAt),
                });
            });

            setPromoCodes(fetched);
        } catch (err) {
            console.error('Error fetching promo codes:', err);
        }
    }, []);

    // Fetch user subscriptions
    const fetchUserSubscriptions = useCallback(async () => {
        try {
            let q = query(collection(db, 'userSubscriptions'), orderBy('createdAt', 'desc'));
            if (selectedBranch) {
                q = query(collection(db, 'userSubscriptions'), where('branchId', '==', selectedBranch), orderBy('createdAt', 'desc'));
            }
            const snapshot = await getDocs(q);
            const fetched: UserSubscription[] = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                fetched.push({
                    id: doc.id,
                    userId: data.userId,
                    userName: data.userName,
                    planId: data.planId,
                    planName: data.planName,
                    planType: data.planType || 'recurring', // Fallback
                    startDate: toDate(data.startDate),
                    endDate: toDate(data.endDate),
                    classLimit: data.classLimit,
                    classesUsed: data.classesUsed || 0,
                    status: data.status,
                    promoCodeUsed: data.promoCodeUsed,
                    amountPaid: data.amountPaid,
                    renewalNotified: data.renewalNotified || false,
                    branchId: data.branchId,
                    createdAt: toDate(data.createdAt),
                });
            });
            setUserSubscriptions(fetched);
        } catch (err) {
            console.error('Error fetching user subscriptions:', err);
        }
    }, [selectedBranch]);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            await Promise.all([fetchPlans(), fetchPromoCodes(), fetchUserSubscriptions()]);
            setLoading(false);
        };
        fetchAll();
    }, [fetchPlans, fetchPromoCodes, fetchUserSubscriptions]);

    // CRUD for Plans
    const createPlan = async (plan: Omit<SubscriptionPlan, 'id' | 'createdAt' | 'updatedAt'>) => {
        const docRef = await addDoc(collection(db, 'subscriptionPlans'), {
            ...plan,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
        });
        await fetchPlans();
        return docRef.id;
    };

    const updatePlan = async (id: string, data: Partial<SubscriptionPlan>) => {
        await updateDoc(doc(db, 'subscriptionPlans', id), {
            ...data,
            updatedAt: serverTimestamp()
        });
        await fetchPlans(); // Refresh to ensure strict sync or just update local state
        // Optimistic update for speed
        setPlans(prev => prev.map(p => p.id === id ? { ...p, ...data, updatedAt: new Date() } : p));
    };

    const deletePlan = async (id: string) => {
        await deleteDoc(doc(db, 'subscriptionPlans', id));
        await fetchPlans();
    };

    // CRUD for Promo Codes
    const createPromoCode = async (promo: Omit<PromoCode, 'id' | 'usedCount' | 'createdAt'>) => {
        const docRef = await addDoc(collection(db, 'promoCodes'), {
            ...promo,
            validFrom: Timestamp.fromDate(promo.validFrom),
            validUntil: Timestamp.fromDate(promo.validUntil),
            usedCount: 0,
            createdAt: Timestamp.now(),
        });
        await fetchPromoCodes();
        return docRef.id;
    };

    const updatePromoCode = async (id: string, updates: Partial<PromoCode>) => {
        const updateData: Record<string, unknown> = { ...updates };
        if (updates.validFrom) updateData.validFrom = Timestamp.fromDate(updates.validFrom);
        if (updates.validUntil) updateData.validUntil = Timestamp.fromDate(updates.validUntil);
        await updateDoc(doc(db, 'promoCodes', id), updateData);
        await fetchPromoCodes();
    };

    const deletePromoCode = async (id: string) => {
        await deleteDoc(doc(db, 'promoCodes', id));
        await fetchPromoCodes();
    };

    // Validate promo code
    const validatePromoCode = (code: string, planId?: string): { valid: boolean; promo?: PromoCode; error?: string } => {
        const promo = promoCodes.find(p => p.code.toUpperCase() === code.toUpperCase());
        if (!promo) return { valid: false, error: 'Invalid promo code' };
        if (!promo.isActive) return { valid: false, error: 'Promo code is inactive' };
        const now = new Date();
        if (now < promo.validFrom) return { valid: false, error: 'Promo code not yet valid' };
        if (now > promo.validUntil) return { valid: false, error: 'Promo code expired' };
        if (promo.maxUses && promo.usedCount >= promo.maxUses) return { valid: false, error: 'Promo code usage limit reached' };
        if (planId && promo.applicablePlanIds && promo.applicablePlanIds.length > 0 && !promo.applicablePlanIds.includes(planId)) {
            return { valid: false, error: 'Promo code not applicable to this plan' };
        }
        return { valid: true, promo };
    };

    // Calculate discounted price
    const calculatePrice = (plan: SubscriptionPlan, promoCode?: PromoCode): number => {
        if (!promoCode) return plan.price;
        if (promoCode.discountType === 'percentage') {
            return plan.price * (1 - promoCode.discountValue / 100);
        }
        return Math.max(0, plan.price - promoCode.discountValue);
    };

    /**
     * Assigns a subscription to a user.
     * Handles payment calculation, promo code validation, and commission generation within a transaction.
     */
    const assignSubscription = async (
        userId: string,
        userName: string,
        planId: string,
        paymentMethod: string,
        promoCode?: string,
        branchId?: string,
        customStartDate?: Date,
        assignedCoachId?: string,
        skipIncomeRecord: boolean = false,
        customAmount?: number, // NEW: Manual override
        paymentDate?: Date,    // NEW: Manual override
        sourceDetail?: string,  // NEW: Class Name for Payouts
        walletDeduction: number = 0, // NEW: Amount to deduct from wallet
        customEndDate?: Date // NEW: Manual override for End Date
    ) => {
        const plan = plans.find(p => p.id === planId);
        if (!plan) throw new Error('Plan not found');

        let promo: PromoCode | undefined;
        if (promoCode) {
            const validation = validatePromoCode(promoCode, planId);
            if (!validation.valid) throw new Error(validation.error);
            promo = validation.promo;
        }

        const startDate = customStartDate || new Date();

        let endDate: Date;
        if (customEndDate) {
            endDate = customEndDate;
        } else {
            endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + plan.durationDays);
        }

        // precise amount paid (custom or calculated)
        const amountPaid = customAmount !== undefined ? customAmount : calculatePrice(plan, promo);

        // precise date for records
        const recordDate = paymentDate ? Timestamp.fromDate(paymentDate) : Timestamp.now();

        await runTransaction(db, async (transaction: Transaction) => {
            // === PHASE 1: ALL READS FIRST ===

            // 1. Fetch Coach data if needed
            let coachName = 'Unknown Coach';
            if (assignedCoachId) {
                const coachUserRef = doc(db, 'users', assignedCoachId);
                const coachUserDoc = await transaction.get(coachUserRef);
                if (coachUserDoc.exists()) {
                    coachName = coachUserDoc.data().displayName || 'Unknown Coach';
                }
            }

            // 2. Fetch Branch data if needed
            let branchName = 'Unknown Branch';
            let revenueSharePct = 0;
            if (branchId) {
                const branchRef = doc(db, 'branches', branchId);
                const branchDoc = await transaction.get(branchRef);
                if (branchDoc.exists()) {
                    const branchData = branchDoc.data();
                    branchName = branchData.name || 'Unknown Branch';
                    revenueSharePct = branchData.commissionPercentage || 0;
                }
            }

            // 3. Fetch Wallet data if needed (MUST BE BEFORE WRITES)
            let currentWalletBalance = 0;
            if (walletDeduction > 0) {
                const walletRef = doc(db, 'wallets', userId);
                const walletSnap = await transaction.get(walletRef);

                if (!walletSnap.exists()) {
                    throw new Error("User has no wallet to deduct from");
                }
                currentWalletBalance = walletSnap.data().balance || 0;

                if (currentWalletBalance < walletDeduction) {
                    throw new Error(`Insufficient wallet balance. Available: ${currentWalletBalance}, Required: ${walletDeduction}`);
                }
            }

            // === PHASE 2: ALL WRITES ===

            // 1. Create UserSubscription
            const subRef = doc(collection(db, 'userSubscriptions'));
            const subscriptionData: Record<string, unknown> = {
                userId,
                userName,
                planId,
                planName: plan.name,
                startDate: Timestamp.fromDate(startDate),
                endDate: Timestamp.fromDate(endDate),
                classLimit: plan.classLimit,
                classesUsed: 0,
                status: 'active',
                amountPaid,
                paymentMethod, // Save payment method for Cloud Function
                renewalNotified: false,
                createdAt: serverTimestamp(),
            };
            if (promoCode) subscriptionData.promoCodeUsed = promoCode;
            if (branchId) subscriptionData.branchId = branchId;
            if (assignedCoachId) subscriptionData.assignedCoachId = assignedCoachId;

            transaction.set(subRef, subscriptionData);

            // 2. Promo Code Usage
            if (promo) {
                const promoRef = doc(db, 'promoCodes', promo.id);
                transaction.update(promoRef, {
                    usedCount: (promo.usedCount || 0) + 1
                });
            }

            // 3. Finance & Commission Logic
            // NEW: Create commission/revenue share records immediately upon subscription (Income Recording)

            // A. Coach Commission
            let commissionPayoutId: string | undefined;
            // Calculate commission based on ACTUAL amount paid, not base plan price
            if (assignedCoachId && amountPaid > 0) {
                const commissionAmount = amountPaid * 0.25;
                const commissionRef = doc(collection(db, 'commission_dues'));
                commissionPayoutId = commissionRef.id;

                transaction.set(commissionRef, {
                    coachId: assignedCoachId,
                    coachName: coachName,
                    studentId: userId,
                    studentName: userName,
                    amount: commissionAmount,
                    payoutSource: 'subscription',
                    sourceId: subRef.id,
                    className: sourceDetail || 'subscription', // NEW: Store class name
                    branchId: branchId || 'main',
                    status: 'pending',
                    createdAt: recordDate // Use manual date
                });
            }

            // B. Branch Revenue Share
            if (branchId && revenueSharePct > 0) {
                const branchAmount = amountPaid * (revenueSharePct / 100);

                // 1. Create Immediate Expense Record (Expenses Tab)
                // We do NOT create a Payout record for Branch (commission_dues) as this is considered an internal transfer/immediate expense.
                if (!skipIncomeRecord) {
                    const expenseRef = doc(collection(db, 'finance'));
                    transaction.set(expenseRef, {
                        type: 'expense',
                        category: `Branch Commission - ${branchName}`,
                        amount: branchAmount,
                        description: `Commission (${revenueSharePct}%) for ${plan.name} (${userName})`,
                        date: recordDate, // Use manual date
                        reference: subRef.id,
                        sourceId: subRef.id,
                        relatedEntity: 'subscription',
                        createdBy: 'system',
                        branchId: branchId,
                        status: 'active', // Confirmed expense
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp()
                    });
                }
            }

            // Mark subscription as processed if commission was created (or even if just branch share?)
            subscriptionData.commissionProcessed = true;
            if (commissionPayoutId) subscriptionData.commissionPayoutId = commissionPayoutId; // Link to coach payout if exists

            // 5. Wallet Deduction
            if (walletDeduction > 0) {
                const walletRef = doc(db, 'wallets', userId);
                // READ IS DONE ABOVE
                const newBalance = currentWalletBalance - walletDeduction;

                transaction.update(walletRef, {
                    balance: newBalance,
                    updatedAt: serverTimestamp()
                });

                // Create Wallet Transaction Record
                const wTxRef = doc(collection(db, 'wallet_transactions'));
                transaction.set(wTxRef, {
                    walletId: userId,
                    userId: userId,
                    type: 'debit',
                    amount: walletDeduction,
                    description: `Pro-rata payment for ${plan.name} subscription`,
                    performedBy: 'system', // or logged in user if available
                    relatedEntityId: subRef.id,
                    scope: 'all',
                    isRefundable: false,
                    timestamp: recordDate
                });
            }

            // 6. Finance Income Record
            // IMPORTANT: Skipped if skipIncomeRecord is true (Manual entry by accountant or Member Self-Reg)
            // Income record reflects CASH received only (Total - Wallet Deduction)
            const cashAmount = Math.max(0, amountPaid - walletDeduction);

            if (!skipIncomeRecord) {
                const incomeRef = doc(collection(db, 'finance'));
                const desc = walletDeduction > 0
                    ? `Subscription: ${plan.name} (${userName}) - Paid split (Cash: ${cashAmount}, Wallet: ${walletDeduction})`
                    : `Subscription: ${plan.name} (${userName})`;

                transaction.set(incomeRef, {
                    type: 'income',
                    category: 'Membership Fees',
                    amount: cashAmount, // Only record active cash income
                    description: desc,
                    date: recordDate, // Income recorded now (or manual date)
                    reference: subRef.id,
                    sourceId: subRef.id, // For Refund Logic
                    relatedEntity: 'subscription',
                    notes: `Plan: ${plan.name}, Promo: ${promoCode || 'None'}. Total Value: ${amountPaid}, Wallet Used: ${walletDeduction}`,
                    createdBy: userId, // Attributed to the user paying
                    branchId: branchId || 'main',
                    status: 'active',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
            }

            // 6. Update User Profile Status
            const userRef = doc(db, 'users', userId);
            transaction.update(userRef, {
                subscriptionStatus: 'active',
                activeSubscriptionId: subRef.id,
                assignedCoachId: assignedCoachId || null,
                updatedAt: serverTimestamp(),
            });
        });

        await fetchUserSubscriptions();
        // Returns nothing as IDs are internal to transaction, 
        // but traditionally we returned ID. We can't easily get the ID out of runTransaction 
        // unless we generated ref outside. We did generate subRef outside!
        return 'transaction-completed';
    };

    // Get user's active subscription
    const getUserSubscription = (userId: string): UserSubscription | undefined => {
        const now = new Date();
        return userSubscriptions.find(sub =>
            sub.userId === userId &&
            sub.status === 'active' &&
            sub.endDate > now &&
            (sub.classLimit === null || sub.classesUsed < sub.classLimit)
        );
    };

    // Increment class usage
    const incrementClassUsage = async (userId: string) => {
        const sub = getUserSubscription(userId);
        if (!sub) return false;

        await updateDoc(doc(db, 'userSubscriptions', sub.id), {
            classesUsed: sub.classesUsed + 1,
        });
        await fetchUserSubscriptions();
        return true;
    };

    // Cancel subscription — Task #23: routes through the subscriptionAdmin
    // callable so the userSubscriptions status update AND the user-doc
    // subscriptionStatus/activeSubscriptionId update happen atomically
    // server-side. Audit logging is also moved server-side (logged with
    // the canonical AuditLogEntry shape inside the callable).
    const cancelSubscription = async (subscriptionId: string) => {
        const call = httpsCallableFromURL<
            { action: 'cancel'; subscriptionId: string },
            { success: boolean; action: 'cancel'; subscriptionId: string }
        >(functions, callableUrl('subscriptionAdmin'));
        await call({ action: 'cancel', subscriptionId });
        await fetchUserSubscriptions();
    };

    // Renew subscription (creates new subscription)
    const renewSubscription = async (userId: string, userName: string, planId: string, paymentMethod: string, branchId?: string) => {
        // Mark old subscriptions as expired
        const userSubs = userSubscriptions.filter(s => s.userId === userId && s.status === 'active');
        for (const sub of userSubs) {
            await updateDoc(doc(db, 'userSubscriptions', sub.id), { status: 'expired' });
        }

        // Create new subscription
        return assignSubscription(userId, userName, planId, paymentMethod, undefined, branchId);
    };

    // Update active subscription (admin edit) — Task #23: route the two
    // sensitive mutations (endDate, classesUsed) through subscriptionAdmin
    // for server-side audit + atomic writes. Other fields (status, planId,
    // etc.) still go through a direct updateDoc, which the rule permits for
    // admin/coach/coordinator. AdjustQuotaDialog and QuotaReconciliation
    // typically only touch classesUsed and endDate, so most calls now
    // round-trip through the callable.
    const updateSubscription = async (id: string, updates: Partial<UserSubscription>) => {
        const subscriptionAdminCall = httpsCallableFromURL<
            Record<string, unknown>,
            { success: boolean }
        >(functions, callableUrl('subscriptionAdmin'));

        if (updates.endDate) {
            await subscriptionAdminCall({
                action: 'changeEndDate',
                subscriptionId: id,
                endDate: updates.endDate.toISOString(),
            });
        }
        if (typeof updates.classesUsed === 'number') {
            await subscriptionAdminCall({
                action: 'adjustClassesUsed',
                subscriptionId: id,
                classesUsed: updates.classesUsed,
            });
        }

        // Any remaining client-touched fields go through a direct update
        // (admin-context only; allowed by the userSubscriptions update rule).
        const remaining: Record<string, unknown> = { ...updates };
        delete remaining.endDate;
        delete remaining.classesUsed;
        if (updates.startDate) {
            remaining.startDate = Timestamp.fromDate(updates.startDate);
        }
        if (Object.keys(remaining).length > 0) {
            await updateDoc(doc(db, 'userSubscriptions', id), remaining);
        }

        // Audit Log (kept client-side here as a thin "I called the admin
        // mutation" marker; the callable already audits the canonical entry).
        logAction({
            action: 'update',
            module: 'subscription',
            targetId: id,
            details: JSON.stringify(Object.keys(updates))
        });

        await fetchUserSubscriptions();
    };

    // Get classes remaining
    const getClassesRemaining = (userId: string): { remaining: number | 'unlimited'; total: number | 'unlimited' } | null => {
        const sub = getUserSubscription(userId);
        if (!sub) return null;
        if (sub.classLimit === null) return { remaining: 'unlimited', total: 'unlimited' };
        return { remaining: sub.classLimit - sub.classesUsed, total: sub.classLimit };
    };

    // Get days until expiry
    const getDaysUntilExpiry = (userId: string): number | null => {
        const sub = getUserSubscription(userId);
        if (!sub) return null;
        const now = new Date();
        const diffMs = sub.endDate.getTime() - now.getTime();
        return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    };

    // Check if subscription needs renewal notification
    const getExpiredSubscriptions = (): UserSubscription[] => {
        const now = new Date();
        return userSubscriptions.filter(sub =>
            sub.status === 'active' && (
                sub.endDate <= now ||
                (sub.classLimit !== null && sub.classesUsed >= sub.classLimit)
            )
        );
    };

    // Fetch subscriptions for a specific user
    const fetchSubscriptionsForUser = useCallback(async (userId: string) => {
        setLoading(true);
        try {
            const q = query(
                collection(db, 'userSubscriptions'),
                where('userId', '==', userId)
            );
            const snapshot = await getDocs(q);
            const fetched: UserSubscription[] = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                fetched.push({
                    id: doc.id,
                    userId: data.userId,
                    userName: data.userName,
                    planId: data.planId,
                    planName: data.planName,
                    planType: data.planType || 'recurring', // Fallback
                    startDate: toDate(data.startDate),
                    endDate: toDate(data.endDate),
                    classLimit: data.classLimit,
                    classesUsed: data.classesUsed || 0,
                    status: data.status,
                    promoCodeUsed: data.promoCodeUsed,
                    amountPaid: data.amountPaid,
                    renewalNotified: data.renewalNotified || false,
                    branchId: data.branchId,
                    createdAt: toDate(data.createdAt),
                });
            });

            // Sort in memory to avoid needing composite index
            fetched.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

            // Update state ONLY if we are setting for the current view context
            // But since this hook is used globally, overwriting 'userSubscriptions' might affect other views if multiple exist.
            // For now, in ParentDashboard, we only view one child at a time, so it's fine to overwrite.
            setUserSubscriptions(fetched);
            return fetched;
        } catch (err) {
            console.error('Error fetching user subscriptions:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    // Reorder plans
    const reorderPlans = async (newOrderPlans: SubscriptionPlan[]) => {
        setPlans(newOrderPlans);
        const batch = writeBatch(db);
        newOrderPlans.forEach((plan, index) => {
            const ref = doc(db, 'subscriptionPlans', plan.id);
            batch.update(ref, { order: index, updatedAt: serverTimestamp() });
        });
        await batch.commit();
    };

    return {
        plans,
        promoCodes,
        userSubscriptions,
        loading,
        createPlan,
        updatePlan,
        deletePlan,
        createPromoCode,
        updatePromoCode,
        deletePromoCode,
        validatePromoCode,
        calculatePrice,
        assignSubscription,
        getUserSubscription,
        getActiveSubscription: getUserSubscription,
        incrementClassUsage,
        cancelSubscription,
        renewSubscription,
        updateSubscription,
        getClassesRemaining,
        getDaysUntilExpiry,
        getExpiredSubscriptions,
        refreshPlans: fetchPlans,
        refreshPromoCodes: fetchPromoCodes,
        refreshSubscriptions: fetchUserSubscriptions,
        fetchSubscriptionsForUser,
        reorderPlans,
    };
};
